"use client";

import { memo, useCallback, useEffect, useId, useState } from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";
import {
  useA2UIComponent,
  type A2UIComponentProps,
  type Types,
} from "@a2ui/react";

export const BeeOSSlider = memo(function BeeOSSlider({
  node,
  surfaceId,
}: A2UIComponentProps<Types.SliderNode>) {
  const { resolveNumber, resolveString, setValue, getValue } =
    useA2UIComponent(node, surfaceId);
  const props = node.properties;
  const id = useId();
  const valuePath = props.value?.path;
  const minValue = props.minValue ?? 0;
  const initialValue = valuePath
    ? (() => { const v = getValue(valuePath); return v != null && !Number.isNaN(Number(v)) ? Number(v) : minValue; })()
    : (resolveNumber(props.value) ?? 0);
  const maxValue = props.maxValue ?? 100;
  const extProps = props as typeof props & { label?: unknown };
  const label = extProps.label
    ? resolveString(extProps.label as Parameters<typeof resolveString>[0])
    : null;

  const [value, setLocalValue] = useState(initialValue);

  useEffect(() => {
    if (valuePath) {
      const externalValue = getValue(valuePath);
      if (externalValue != null && !Number.isNaN(Number(externalValue)) && Number(externalValue) !== value) {
        setLocalValue(Number(externalValue));
      }
    }
  }, [valuePath, getValue]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (props.value?.literalNumber !== undefined) {
      setLocalValue(props.value.literalNumber);
    }
  }, [props.value?.literalNumber]);

  const handleChange = useCallback(
    (values: number[]) => {
      const newValue = values[0] ?? 0;
      setLocalValue(newValue);
      if (valuePath) setValue(valuePath, newValue);
    },
    [valuePath, setValue],
  );

  const hostStyle: React.CSSProperties =
    node.weight !== undefined
      ? ({ "--weight": node.weight } as React.CSSProperties)
      : {};

  return (
    <div className="a2ui-slider" style={hostStyle}>
      <div className="flex flex-col gap-2">
        {label && (
          <div className="flex items-center justify-between">
            <label
              htmlFor={id}
              className="text-sm font-medium leading-none text-foreground"
            >
              {label}
            </label>
            <span className="text-sm tabular-nums text-muted-foreground">
              {value}
            </span>
          </div>
        )}
        <SliderPrimitive.Root
          id={id}
          min={minValue}
          max={maxValue}
          step={1}
          value={[value]}
          onValueChange={handleChange}
          className="relative flex w-full touch-none select-none items-center"
        >
          <SliderPrimitive.Track className="relative h-2 w-full grow overflow-hidden rounded-full bg-secondary/80">
            <SliderPrimitive.Range className="absolute h-full bg-primary" />
          </SliderPrimitive.Track>
          <SliderPrimitive.Thumb className="block h-5 w-5 rounded-full border-2 border-primary bg-background shadow-[0_1px_3px_rgba(0,0,0,0.12)] dark:shadow-[0_1px_3px_rgba(0,0,0,0.3)] ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50" />
        </SliderPrimitive.Root>
      </div>
    </div>
  );
});
